import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchUsers } from '../../hooks/useFetchUsers';
import { MatterRequestUserField } from '../../hooks/useSelectMatterRequestUser';
import { EntityStatus } from '../../interface/common';
import { IMatterRequest } from '../../interface/matterRequest';
import { IUser } from '../../interface/user';

/** The props `SearchableSelect` needs to pick a matter request's owner or responsible person, from the same list the picker screen uses. */
export const useMatterRequestUserSelect = (field: MatterRequestUserField) => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: users,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchUsers({
    pageSize: 20,
    sortBy: ['firstNames:ASC'],
    search: searchValue,
    extraFilters: { status: EntityStatus.ACTIVE },
  });

  const options: SelectOption[] = users.map((user: IUser) => ({
    value: user.id,
    label: user.fullName,
  }));

  const chosenUserId =
    field === 'owner' ? matterRequest?.ownerUserId : matterRequest?.responsibleUserId;
  const chosenUser = field === 'owner' ? matterRequest?.ownerUser : matterRequest?.responsibleUser;
  const value: SelectOption | null = chosenUserId
    ? { value: chosenUserId, label: chosenUser?.fullName ?? '' }
    : null;

  const onChange = (option: SelectOption) => {
    const user = users.find(({ id }: IUser) => id === option.value);
    if (!user) return;
    updateManageMatterRequest({
      ...matterRequest,
      ...(field === 'owner'
        ? { ownerUserId: user.id, ownerUser: user, isOwnerSetByUser: true }
        : { responsibleUserId: user.id, responsibleUser: user, isResponsibleSetByUser: true }),
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
